import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Calendar, LogOut, Ticket, Users, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { API } from "../config/api";

export default function MemberDashboard() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("member_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API}/member/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMember(data);
      } else {
        localStorage.removeItem("member_token");
        localStorage.removeItem("member_data");
        navigate("/login");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_data");
    toast.success("Logged out successfully");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-kerala-cream flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-kerala-cream" data-testid="member-dashboard">
      {/* Hero */}
      <section className="py-12 bg-kerala-green">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-kerala-gold rounded-full flex items-center justify-center">
                <span className="font-heading text-kerala-green font-bold text-2xl">
                  {member?.name?.charAt(0) || "M"}
                </span>
              </div>
              <div>
                <p className="text-kerala-gold text-sm tracking-widest uppercase mb-1">Welcome back</p>
                <h1 className="font-heading text-3xl text-white">
                  {member?.name || "Member"}
                </h1>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-kerala-green"
              data-testid="member-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section className="section-padding">
        <div className="section-container">
          {/* Welcome Card */}
          <div className="bg-white rounded-sm shadow-lg p-8 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-kerala-green" />
              </div>
              <div>
                <h2 className="font-heading text-2xl text-kerala-green">
                  Thank you for being a member!
                </h2>
                <p className="text-gray-600">
                  You're part of the Tatvamasi community.
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              As a registered member of Tatvamasi - Kerala Hindu's of London Ontario, you're connected 
              to a vibrant community dedicated to preserving and celebrating Kerala's rich cultural heritage.
            </p>
          </div>

          {/* Quick Links */}
          <h3 className="font-heading text-xl text-kerala-green mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Link to="/my-tickets" className="group">
              <div className="bg-white rounded-sm shadow p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-kerala-gold/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-kerala-gold/30 transition-colors">
                  <Ticket className="w-6 h-6 text-kerala-gold" />
                </div>
                <h4 className="font-heading text-lg text-kerala-green mb-2">My Tickets</h4>
                <p className="text-gray-600 text-sm">
                  View and download your purchased tickets
                </p>
              </div>
            </Link>

            <Link to="/picnic-2026" className="group">
              <div className="bg-white rounded-sm shadow p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-kerala-terracotta/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-kerala-terracotta/30 transition-colors">
                  <Calendar className="w-6 h-6 text-kerala-terracotta" />
                </div>
                <h4 className="font-heading text-lg text-kerala-green mb-2">Book Tickets</h4>
                <p className="text-gray-600 text-sm">
                  Get tickets for the Annual Picnic 2026
                </p>
              </div>
            </Link>

            <Link to="/about" className="group">
              <div className="bg-white rounded-sm shadow p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-kerala-green/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-kerala-green/30 transition-colors">
                  <Users className="w-6 h-6 text-kerala-green" />
                </div>
                <h4 className="font-heading text-lg text-kerala-green mb-2">About Us</h4>
                <p className="text-gray-600 text-sm">
                  Learn more about our community
                </p>
              </div>
            </Link>

            <Link to="/gallery" className="group">
              <div className="bg-white rounded-sm shadow p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-kerala-green/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-kerala-green/30 transition-colors">
                  <Calendar className="w-6 h-6 text-kerala-green" />
                </div>
                <h4 className="font-heading text-lg text-kerala-green mb-2">Gallery</h4>
                <p className="text-gray-600 text-sm">
                  View memories from past events
                </p>
              </div>
            </Link>
          </div>

          {/* Member Info */}
          <div className="mt-8 bg-white rounded-sm shadow p-6">
            <h3 className="font-heading text-xl text-kerala-green mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Profile
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="text-kerala-green font-medium">{member?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-kerala-green font-medium">{member?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Member Since</p>
                <p className="text-kerala-green font-medium">
                  {member?.created_at 
                    ? new Date(member.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
